import { AxiosResponse } from 'axios';
import { Option } from '~/components/Select';
import Address, { fromJSON as addressFromJSON, JSONAddress } from '~/types/Address';
import { TrackingReference } from '~/utils/TrackingHelper/types';

export const MANIFEST_FAQ_LINK =
  'https://support.goshippo.com/hc/en-us/articles/204520339-How-do-I-print-an-end-of-day-report-manifest-for-a-particular-carrier-';

type Manifest = {
  objectId: string;
  id?: number;
  objectStatus: ManifestStatus;
  providerToken: string;
  carrier?: string;
  documents: string[];
  errors: string[];
  submissionDate: Date;
  isTest?: Maybe<boolean>;
  addressFrom: string;
};

type JSONManifest = {
  object_id: string;
  id?: number;
  object_status: ManifestStatus;
  provider: string;
  carrier?: string;
  documents: string[];
  errors: string[];
  shipment_date: string;
  // TODO: hydrate this on the BE
  address_from: string;
};

function ManifestFromJSON(manifestJSON: JSONManifest): Manifest {
  return {
    objectId: manifestJSON.object_id,
    id: manifestJSON.id,
    objectStatus: manifestJSON.object_status,
    providerToken: manifestJSON.provider,
    carrier: manifestJSON.carrier,
    documents: manifestJSON.documents,
    errors: manifestJSON.errors,
    submissionDate: new Date(manifestJSON.shipment_date),
    addressFrom: manifestJSON.address_from,
  };
}

type ManifestsGetResponse = AxiosResponse<{
  results: JSONManifest[];
}>;

type ManifestsCreateType = {
  dateRangeStart: Date;
  dateRangeEnd?: Date;
  carrierName: Option;
  address: Option;
};

type ManifestsTransactionCount = {
  count: number;
  servicelevelName: string;
};

type ManifestServiceLevelCounts = {
  dateRangeStart: Date;
  dateRangeEnd: Date;
  submissionDate: Date;
  manifestableTransactionCount: ManifestsTransactionCount[];
};
type JSONManifestServiceLevelCounts = {
  date_range_start: Date;
  date_range_end: Date;
  submission_date: Date;
  manifestable_transaction_count: {
    count: number;
    servicelevel_name: string;
  }[];
};

function ManifestServiceLevelCountsFromJSON(
  jsonManifestServiceLevelCounts: JSONManifestServiceLevelCounts,
): ManifestServiceLevelCounts {
  return {
    dateRangeStart: jsonManifestServiceLevelCounts.date_range_start,
    dateRangeEnd: jsonManifestServiceLevelCounts.date_range_end,
    submissionDate: jsonManifestServiceLevelCounts.submission_date,
    manifestableTransactionCount: jsonManifestServiceLevelCounts.manifestable_transaction_count.map(
      (entry) => ({
        count: entry.count,
        servicelevelName: entry.servicelevel_name,
      }),
    ),
  };
}

type ManifestsDateRangeByAddress = {
  [key: string]: ManifestServiceLevelCounts[];
};

type JSONManifestsDateRangeByAddress = {
  [key: string]: JSONManifestServiceLevelCounts[];
};

type ManifestGroupsResponse = AxiosResponse<{
  results: JSONManifestGroups;
}>;

export type ManifestGroupsRequestPayload = {
  carrierObjectId: string;
  submissionDate?: Date;
  dateRangeEnd?: Date;
  dateRangeStart?: Date;
};

type JSONManifestGroups = {
  carrier_account: string;
  addresses: JSONAddress[];
  ranges: JSONManifestsDateRangeByAddress;
};

type ManifestGroups = {
  carrierAccount: string;
  addresses: Address[];
  ranges: ManifestsDateRangeByAddress;
};

function ManifestGroupsFromJSON(jsonManifestGroups: JSONManifestGroups): ManifestGroups {
  return {
    carrierAccount: jsonManifestGroups.carrier_account,
    addresses: jsonManifestGroups.addresses.map(
      (jsonAddress) => addressFromJSON(jsonAddress) as Address,
    ),
    ranges: Object.entries(jsonManifestGroups.ranges).reduce(
      (rangesMap: ManifestsDateRangeByAddress, [key, jsonServiceLevelCountsList]) => {
        rangesMap[key] = jsonServiceLevelCountsList.map(ManifestServiceLevelCountsFromJSON);
        return rangesMap;
      },
      {},
    ),
  };
}

//OBJECT_STATUS_CHOICE = Choices('QUEUED', 'SUCCESS', 'ERROR')
enum ManifestStatus {
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

type ManifestGeneratePayload = {
  address_from: Address;
  async: boolean;
  carrier_account: string;
  object_updated?: string;
  object_created?: string;
  submission_date: string;
  trackingReference: TrackingReference;
};

export {
  ManifestsCreateType,
  ManifestServiceLevelCounts,
  ManifestsDateRangeByAddress,
  ManifestGroups,
  ManifestStatus,
  JSONManifestGroups,
  ManifestGroupsResponse,
  ManifestsTransactionCount,
  ManifestGeneratePayload,
  ManifestGroupsFromJSON,
  ManifestFromJSON,
  JSONManifest,
  ManifestsGetResponse,
};
export default Manifest;
